/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'

import {
  Heading,
  Button,
  Select,
  RadioGroup,
  FixedTextInput as PaymentAmountInput,
  PayrollCycleFrequency,
  SpanWithData
} from '../'

import {
  showAsCurrency
} from '../../utils'


export class IdentityPaymentsEdit extends React.Component {
  static propTypes = {
    heading: PropTypes.string.isRequired,
    payments: PropTypes.arrayOf(PropTypes.object).isRequired,
    payrollCycleId: PropTypes.string.isRequired,
    globalActions: PropTypes.element,
    showProductWarning: PropTypes.bool,
    populateAmountInputsWithZero: PropTypes.bool,
    getReasonForChange: PropTypes.func.isRequired,
    getPaymentAmountChanged: PropTypes.func.isRequired,
    onAmountChange: PropTypes.func.isRequired,
    onReasonChange: PropTypes.func.isRequired,
    onResetToInitial: PropTypes.func,
    displayResetToInitial: PropTypes.bool,
    className: PropTypes.string
  }

  getReduceAllProductWarning = (product, source) => {
    switch(product) {
      case "SAVING_SHORTTERM":
        return "No investment will be made this payroll cycle."

      case "SAVING_LONGTERM":
        return "No investment will be made this payroll cycle."

      case "LOAN":
        return "Changing this will result in longer payment term and potentially extra fees for the client."

      case "INSURANCE_FUNERAL":
        if (source === "CLIENT") {  // employee - voluntary funeral cover
          return "During waiting period: waiting period will be extended. After waiting period: no cover for the duration of the next payroll cycle."
        } else {  // employer - compulsory funeral cover
          return "This client will/might not be covered in the event that a death occurs during the cover period that he/she skipped."
        }

      default:
        return ''
    }
  }

  setPaymentReasons = (payments, reason) => {
    this.props.onReasonChange(reason, payments.map(payment => payment.node.id), true)
  }

  render() {
    if (this.props.payments.length > 0) {
      let insuranceFuneralPayments = this.props.payments.filter(payment => payment.node.product === 'INSURANCE_FUNERAL')
      let paymentsExclInsuranceFuneral = this.props.payments.filter(payment => payment.node.product !== 'INSURANCE_FUNERAL')

      return (
        <div className={this.props.className}>
          <div className="deduction-payments__heading slds-grid">
            <Heading marginBottom={7}>
              {this.props.heading}
            </Heading>
            {this.props.displayResetToInitial &&
              <div className="slds-col_bump-left slds-text-color_weak">
                Amounts have been changed
                <Button
                  onClick={() => this.props.onResetToInitial()}
                  className="slds-m-left_medium slds-m-bottom_xx-small">
                  RESET TO INITIAL
                </Button>
              </div>
            }
          </div>
          {this.props.globalActions}
          {paymentsExclInsuranceFuneral.map((payment, index) => (
            <PaymentEdit
              payment={payment.node}
              payrollCycleId={this.props.payrollCycleId}
              getReasonForChange={paymentId => this.props.getReasonForChange(paymentId)}
              amountChange={amount => this.props.onAmountChange(amount, payment.node.id)}
              reasonChange={reason => this.props.onReasonChange(reason, payment.node.id)}
              editable={payment.node.editable}
              getPaymentAmountChanged={paymentId => this.props.getPaymentAmountChanged(paymentId)}
              getReduceAllProductWarning={this.getReduceAllProductWarning}
              showProductWarning={this.props.showProductWarning}
              populateAmountInputsWithZero={this.props.populateAmountInputsWithZero}
              key={`paymentEdit_${payment.node.id}`}
            />
          ))}
          {insuranceFuneralPayments.length > 0 &&
            <InsuranceFuneralPaymentsEdit
              payments={insuranceFuneralPayments}
              payrollCycleId={this.props.payrollCycleId}
              setPaymentReasons={reason => this.setPaymentReasons(insuranceFuneralPayments, reason)}
              getReasonForChange={paymentId => this.props.getReasonForChange(paymentId)}
              amountChange={this.props.onAmountChange}
              reasonChange={this.props.onReasonChange}
              revertChanges={this.props.onResetToInitial}
              showProductWarning={this.props.showProductWarning}
              getReduceAllProductWarning={this.getReduceAllProductWarning}
              populateAmountInputsWithZero={this.props.populateAmountInputsWithZero}
              key={`paymentEdit_${insuranceFuneralPayments.map(payment => payment.node.id)}`}
            />
          }
        </div>
      )
    } else {
      return null
    }
  }
}

class InsuranceFuneralPaymentsEdit extends React.Component {
  static propTypes = {
    payments: PropTypes.arrayOf(PropTypes.object).isRequired,
    payrollCycleId: PropTypes.string.isRequired,
    reasonForChange: PropTypes.string,
    amountChange: PropTypes.func.isRequired,
    reasonChange: PropTypes.func.isRequired,
    revertChanges: PropTypes.func.isRequired,
    showProductWarning: PropTypes.bool,
    populateAmountInputsWithZero: PropTypes.bool
  }

  constructor(props) {
    super(props)

    this.state = {
      amounts: this.getPaymentAmounts(props.payments)
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.populateAmountInputsWithZero && nextProps.populateAmountInputsWithZero) {
      this.zeroPaymentAmounts(nextProps.payments)
    } else if (this.props.populateAmountInputsWithZero && !nextProps.populateAmountInputsWithZero) {
      this.setState({ amounts: this.getPaymentAmounts(nextProps.payments) })
    } else if (JSON.stringify(this.getPaymentAmounts(this.props.payments)) !== JSON.stringify(this.getPaymentAmounts(nextProps.payments))) {
      this.setState({ amounts: this.getPaymentAmounts(nextProps.payments) })
    }
  }

  zeroPaymentAmounts = (payments) => {
    this.setState({ amounts: payments.map(payment => 0) }, () => {
      this.props.amountChange('0', payments.map(payment => payment.node.id), true)
    })
  }
  restorePaymentAmounts = (payments) => {
    this.setState({ amounts: payments.map(payment => payment.node.amount) }, () => {
      this.props.revertChanges(payments)
    })
  }

  getPaymentAmounts = payments => payments.map(payment => payment.node.amount)
  getPaymentsTotal = payments => payments.reduce((prev, cur) => prev + parseFloat(cur.node.amount), 0)
  getPaymentFrequency = payment => (
    <PayrollCycleFrequency
      payrollCycle={this.props.payrollCycleId}
      key={`payrollFrequency_payment-${payment.id}`}
    />
  )

  hasPaymentAmountsChanged = () => (
    JSON.stringify(this.getPaymentAmounts(this.props.payments)) !== JSON.stringify(this.state.amounts)
  )

  getReasonForChange = (payment) => this.props.getReasonForChange(payment)

  render() {
    let totalPaymentAmount = showAsCurrency('R', this.getPaymentsTotal(this.props.payments))
    let paymentFrequency = this.getPaymentFrequency(this.props.payments[0].node)
    return (
      <div className={`slds-grid slds-grid_pull-padded-medium slds-border_bottom${this.props.showProductWarning ? '' : ' slds-wrap'}`}>
        <div className="slds-size_1-of-2 slds-p-around_large">
          <div className="deduction-payments__product">
            Funeral Cover
          </div>
          <RadioGroup
            id={this.props.payments.map(payment => payment.node.id).toString()}
            options={[
              {
                label: <span>
                  <b>{totalPaymentAmount}</b>
                  &nbsp;
                  {paymentFrequency}
                </span>,
                value: totalPaymentAmount
              },
              {
                label: "Make R0 for this deduction",
                value: 0
              }
            ]}
            value={this.state.amounts[0] === 0
              ? '0'
              : totalPaymentAmount
            }
            gridLayout={true}
            onClick={(e) => {
              let amount = e.target.value
              if (amount === '0') {
                this.zeroPaymentAmounts(this.props.payments)
              } else {
                this.restorePaymentAmounts(this.props.payments)
              }
            }}
            disabled={this.props.showProductWarning}
          />
        </div>
        {this.props.showProductWarning && this.props.payments[0].node.amount !== 0
          ? <div
              className="slds-size_1-of-2 slds-m-top_large slds-m-left_medium slds-m-right_medium slds-text-color_error"
              style={{ whiteSpace: "normal" }}>
              {this.props.getReduceAllProductWarning('INSURANCE_FUNERAL', this.props.payments[0].node.source)}
            </div>
          : <div className="slds-size_1-of-4 slds-p-top_large slds-p-left_large slds-p-right_large">
              <Select
                label="Reason for change"
                placeholder="Choose"
                value={this.getReasonForChange(this.props.payments[0].node.id)}
                options={[
                  "Reduced wage - weather",
                  "Reduced wage - illness",
                  "Reduced wage - non-attendance",
                  "Reduced wage - public holiday(s)",
                  "Reduced/No wage - holiday",
                  "Contract break",
                  "Excessive farm deductions",
                  "Other"
                ]}
                onChange={e => this.props.setPaymentReasons(e.target.value)}
                disabled={!this.hasPaymentAmountsChanged()}
              />
            </div>
        }
      </div>
    )
  }
}

class PaymentEdit extends React.Component {
  static propTypes = {
    payment: PropTypes.object.isRequired,
    payrollCycleId: PropTypes.string.isRequired,
    reasonForChange: PropTypes.string,
    amountChange: PropTypes.func.isRequired,
    reasonChange: PropTypes.func.isRequired,
    getPaymentAmountChanged: PropTypes.func.isRequired,
    showProductWarning: PropTypes.bool,
    populateAmountInputsWithZero: PropTypes.bool,
    getReduceAllProductWarning: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)

    this.state = {
      amount: props.editable ? '' : props.payment.amount
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.populateAmountInputsWithZero && nextProps.populateAmountInputsWithZero) {
      this.setState({ amount: 0 })
    } else if (this.props.populateAmountInputsWithZero && !nextProps.populateAmountInputsWithZero) {
      this.setState({
        amount: nextProps.editable ? '' : nextProps.payment.amount
      })
    } else if (this.props.payment.amount !== nextProps.payment.amount){
      this.setState({
        amount: nextProps.editable ? '' : nextProps.payment.amount
      })
    }
  }

  setPaymentAmount = (amount) => {
    this.setState({ amount })
    this.props.amountChange(amount)
  }

  selectReasonForChange = () => {
    let reasonForChange = this.props.getReasonForChange(this.props.payment.id)
    let paymentAmountChanged = this.props.getPaymentAmountChanged(this.props.payment.id)

    return (
      <Select
        label="Reason for change"
        placeholder="Choose"
        value={reasonForChange}
        options={[
          "Reduced wage - weather",
          "Reduced wage - illness",
          "Reduced wage - non-attendance",
          "Reduced wage - public holiday(s)",
          "Reduced/No wage - holiday",
          "Contract break",
          "Excessive farm deductions",
          "Other"
        ]}
        onChange={(e) => this.props.reasonChange(e.target.value)}
        disabled={!paymentAmountChanged}
      />
    )
  }

  render() {
    let payment = this.props.payment
    let product = payment.prod.name
    let amount = showAsCurrency('R', payment.amount)
    let frequency = <PayrollCycleFrequency payrollCycle={this.props.payrollCycleId} />
    let reduceAllProductWarning = this.props.getReduceAllProductWarning(payment.product, payment.source)

    if (payment.zeroable && !payment.editable && payment.amount !== 0) {
      return (
        <div className="slds-grid slds-grid_pull-padded-medium slds-border_bottom">
          <div className="slds-size_1-of-2 slds-p-around_large">
            <div className="deduction-payments__product">
              {product}
            </div>
            <RadioGroup
              id={payment.id}
              options={[
                {
                  label: <span>
                    <b>{amount}</b>
                    &nbsp;
                    {frequency}
                  </span>,
                  value: payment.amount
                },
                {
                  label: "Make R0 for this deduction",
                  value: 0
                }
              ]}
              value={this.state.amount}
              gridLayout={true}
              onClick={e => this.setPaymentAmount(e.target.value)}
              disabled={this.props.showProductWarning}
            />
          </div>
          {this.props.showProductWarning && !payment.editable && payment.amount !== 0
            ? <div
                className="slds-size_1-of-2 slds-m-top_large slds-m-left_medium slds-m-right_medium slds-text-color_error"
                style={{ whiteSpace: "normal" }}>
                {reduceAllProductWarning}
              </div>
            : <div className="slds-size_1-of-4 slds-p-around_large">
                {this.selectReasonForChange()}
              </div>
          }
        </div>
      )
    } else if (payment.editable) {
      return (
        <div className="slds-grid slds-grid_pull-padded-medium slds-border_bottom">
          <div className="slds-size_1-of-4 slds-p-around_large">
            <div className="deduction-payments__product">
              {product}
            </div>
            <div>
              <b>{amount}</b>
              &nbsp;
              {frequency}
            </div>
          </div>
          <div className="slds-size_1-of-4 slds-p-top_large slds-p-right_large slds-p-bottom_large">
            {this.props.populateAmountInputsWithZero
              ? <PaymentAmountInput
                  label="New amount"
                  text='R'
                  value='0'
                  disabled={true}
                  onChange={null}
                  maxWidth={130}
                />
              : <PaymentAmountInput
                  label="New amount"
                  text='R'
                  value={this.state.amount}
                  onChange={e => this.setPaymentAmount(e.target.value)}
                  maxWidth={130}
                />
            }
          </div>
          {this.props.showProductWarning
            ? <div
                className="slds-size_1-of-2 slds-m-top_x-large slds-m-left_medium slds-m-right_medium slds-text-color_error"
                style={{ whiteSpace: "normal" }}>
                {reduceAllProductWarning}
              </div>
            : <div className="slds-size_1-of-4 slds-p-around_large">
                {this.selectReasonForChange()}
              </div>
          }
        </div>
      )
    } else {
      return (
        <div className="slds-grid slds-grid_pull-padded-medium slds-border_bottom">
          <div className="slds-size_1-of-4 slds-p-around_large">
            <div className="deduction-payments__product">
              {product}
            </div>
            <div>
              <b>{amount}</b>
              &nbsp;
              {frequency}
            </div>
          </div>
        </div>
      )
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/identity-payments-edit/index.js